/**
 * JointJS Element Factory
 * Creates JointJS elements from CSV data
 */

import { dia, shapes } from '@joint/core'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'
import {
  BanpoRow,
  GroupedEntity,
  transformCoordinates,
  coordsToSVGPath
} from '../../../map-editor-shared/lib/csvParser'
import {
  getAssetPath,
  getLayerFillColor,
  getLayerStrokeColor,
  getIconSize,
  isPointLayer,
  isPolygonLayer,
  isLineLayer,
  isTextLayer
} from '../../../map-editor-shared/lib/assetMapper'

/**
 * Create elements from grouped entities
 */
/**
 * 반포(banpo) 현장 import 결과의 일반 주차구역(p-parking-basic) 단변 길이(픽셀).
 * 이 값을 모든 현장의 표준으로 삼아, 도면별 단위/스케일 차이와 무관하게
 * 일반 주차구역이 항상 동일한 크기(≈198.32 × 396.64)로 렌더링되게 한다.
 *
 * baseScale = STANDARD_PARKING_SHORT_PX / (도면 내 일반 주차구역 단변 중앙값)
 *   - osong(2.5m) → 198.32 / 2.5 = 79.33
 *   - central(2.6m) → 198.32 / 2.6 = 76.28
 *   - banpo(2.5m) → 198.32 / 2.5 = 79.33 (기존과 동일)
 */
const STANDARD_PARKING_SHORT_PX = 198.32

/**
 * 주차구역 polygon 의 단변 길이 측정. 직사각형이 아닐 수도 있으니
 * AABB(축정렬 bbox) 의 짧은 변을 사용한다.
 */
function measureParkingShortSide(entity: GroupedEntity): number | null {
  if (!entity.isPolygon || entity.points.length < 4) return null
  const xs = entity.points.map(p => p.x)
  const ys = entity.points.map(p => p.y)
  const w = Math.max(...xs) - Math.min(...xs)
  const h = Math.max(...ys) - Math.min(...ys)
  if (w <= 0 || h <= 0) return null
  return Math.min(w, h)
}

/**
 * 도면 내 일반 주차구역(우선 p-parking-basic, 없으면 가장 많은 p-parking-* 레이어)
 * 의 단변 길이 중앙값을 구한다.
 */
function calcMedianParkingShort(entities: GroupedEntity[]): number | null {
  const byLayer = new Map<string, number[]>()
  for (const e of entities) {
    if (!e.layer.startsWith('p-parking')) continue
    const short = measureParkingShortSide(e)
    if (short === null) continue
    if (!byLayer.has(e.layer)) byLayer.set(e.layer, [])
    byLayer.get(e.layer)!.push(short)
  }
  if (byLayer.size === 0) return null

  // p-parking-basic 우선, 없으면 샘플 수가 가장 많은 레이어
  const preferred = byLayer.get('p-parking-basic')
  const chosen = preferred && preferred.length > 0
    ? preferred
    : [...byLayer.values()].sort((a, b) => b.length - a.length)[0]

  const sorted = [...chosen].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

export function createElementsFromEntities(
  entities: GroupedEntity[],
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
): dia.Element[] {
  const elements: dia.Element[] = []

  const dataWidth = bounds.maxX - bounds.minX
  const dataHeight = Math.abs(bounds.maxY - bounds.minY)

  // 일반 주차구역 단변 길이를 기준으로 baseScale 결정.
  // 측정 실패(주차구역 없음) 시에만 기존 도면-폭 기준 폴백.
  const medianParkingShort = calcMedianParkingShort(entities)
  let baseScale: number
  let scaleSource: string
  if (medianParkingShort && medianParkingShort > 0) {
    baseScale = STANDARD_PARKING_SHORT_PX / medianParkingShort
    scaleSource = `parking-based (median short=${medianParkingShort.toFixed(3)})`
  } else {
    const TARGET_WIDTH = 1000
    baseScale = TARGET_WIDTH / dataWidth
    scaleSource = `fallback (TARGET_WIDTH=${TARGET_WIDTH} / dataWidth)`
  }

  log.info(LogCategory.GENERAL, 'Data bounds', { width: dataWidth.toFixed(0), height: dataHeight.toFixed(0) })
  log.info(LogCategory.GENERAL, 'Scale source', { scaleSource })
  log.info(LogCategory.GENERAL, 'Scale factor', { scaleFactor: baseScale.toFixed(4) })
  log.info(LogCategory.GENERAL, 'Scaled size', { scaledSize: `${(dataWidth * baseScale).toFixed(0)} x ${(dataHeight * baseScale).toFixed(0)}` })

  const transformBounds = {
    ...bounds,
    scale: baseScale
  }

  entities.forEach(entity => {
    try {
      const element = createElementFromEntity(entity, transformBounds)
      if (element) {
        elements.push(element)
      }
    } catch (error) {
      log.error(LogCategory.GENERAL, `Error creating element for ${entity.layer}`, error)
    }
  })

  log.info(LogCategory.GENERAL, `Created ${elements.length} elements from ${entities.length} entities`)
  return elements
}

/**
 * Calculate centroid of a polygon
 */
function calculateCentroid(points: BanpoRow[]): { x: number; y: number } {
  let sumX = 0
  let sumY = 0

  points.forEach(point => {
    sumX += point.x
    sumY += point.y
  })

  return {
    x: sumX / points.length,
    y: sumY / points.length
  }
}

/**
 * Create a single element from entity
 */
function createElementFromEntity(
  entity: GroupedEntity,
  bounds: { minX: number; minY: number; maxX: number; maxY: number; scale: number; flipX?: boolean }
): dia.Element | null {
  const { layer, points } = entity

  // Skip empty entities
  if (points.length === 0) return null

  // Handle point entities (CCTV, emergency bells, sensors, etc.)
  // These should render as icons/assets even if they have zero size
  if (isPointLayer(layer) || entity.isPoint) {
    // For point entities, create an icon element at the centroid
    return createPointElementFromPolygon(entity, bounds)
  }

  // Text layers (labels)
  if (isTextLayer(layer)) {
    return createTextElement(entity, bounds)
  }

  // Line layers (outlines, lighting) — check BEFORE isClosed to ensure stroke-only rendering
  if (isLineLayer(layer)) {
    return createLineElement(entity, bounds)
  }

  // Closed polygon: render as polygon regardless of layer name (CCTV, COLUMN, 주차면 등)
  if (entity.isClosed && points.length >= 3) {
    return createPolygonElement(entity, bounds)
  }

  // Polygon layers (parking spots, zones) – kept for explicit layer types
  if (isPolygonLayer(layer) && entity.isPolygon) {
    return createPolygonElement(entity, bounds)
  }

  return null
}

/**
 * Create polygon element (parking areas, zones)
 */
function createPolygonElement(
  entity: GroupedEntity,
  bounds: { minX: number; minY: number; maxX: number; maxY: number; scale: number; flipX?: boolean }
): dia.Element {
  const { layer, points, entityHandle } = entity
  // Use standardized ID if available, otherwise fall back to entityHandle
  const elementId = entity.id || entityHandle || `entity_${Date.now()}_${Math.random()}`

  // Transform all coordinates
  const transformedCoords = points.map(p =>
    transformCoordinates(p.x, p.y, { minX: bounds.minX, minY: bounds.minY, maxX: bounds.maxX, maxY: bounds.maxY, scale: bounds.scale })
  )

  // Calculate bounding box for positioning
  const xs = transformedCoords.map(p => p.x)
  const ys = transformedCoords.map(p => p.y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)
  const maxX = Math.max(...xs)
  const maxY = Math.max(...ys)
  const width = maxX - minX
  const height = maxY - minY

  // Convert to relative coordinates for the path
  const relativeCoords = transformedCoords.map(p => ({
    x: p.x - minX,
    y: p.y - minY
  }))

  // Generate SVG path using relative coordinates
  const pathData = coordsToSVGPath(relativeCoords)

  // Create polygon element with explicit position and size
  // Use standardized ID if available
  return new shapes.standard.Path({
    id: elementId,
    position: { x: minX, y: minY },
    size: { width: width || 1, height: height || 1 },
    attrs: {
      body: {
        d: pathData,
        fill: getLayerFillColor(layer),
        stroke: getLayerStrokeColor(layer),
        strokeWidth: 2,
        opacity: 0.8
      },
      label: {
        text: '',
        fill: '#ffffff',
        fontSize: 12,
        refY: '50%',
        refX: '50%',
        textAnchor: 'middle',
        textVerticalAnchor: 'middle'
      }
    },
    data: {
      layer,
      entityHandle,
      originalId: entity.originalId || entityHandle,
      objectType: entity.objectType || layer,
      type: 'polygon',
      vertices: transformedCoords.map(p => [p.x, p.y] as [number, number]),
      // Snapshot of element.position() at creation time so elementConverter can
      // detect drag movement and apply delta to vertices on export.
      // Without this, dragging a polygon updates element.position() but vertices
      // stay frozen → export shows pre-drag coords.
      originalPosition: { x: minX, y: minY },
      properties: {
        name: entityHandle || ''
      }
    }
  })
}

/**
 * Create point element from polygon (e.g., e-onepassreader)
 * Places asset icon at the centroid of the closed shape
 */
function createPointElementFromPolygon(
  entity: GroupedEntity,
  bounds: { minX: number; minY: number; maxX: number; maxY: number; scale: number; flipX?: boolean }
): dia.Element {
  const { layer, points, entityHandle } = entity
  // Use standardized ID if available
  const elementId = entity.id || entityHandle || `point_${Date.now()}_${Math.random()}`

  // Calculate centroid of the points
  // For single point entities, use that point directly
  let centerX: number, centerY: number
  if (points.length === 1) {
    centerX = points[0].x
    centerY = points[0].y
  } else {
    const centroid = calculateCentroid(points)
    centerX = centroid.x
    centerY = centroid.y
  }

  const pos = transformCoordinates(centerX, centerY, { minX: bounds.minX, minY: bounds.minY, maxX: bounds.maxX, maxY: bounds.maxY, scale: bounds.scale })
  let size = getIconSize(layer)
  // CCTV·비상벨 아이콘 크기(2×2)는 미터 단위 관례다. 도면 스케일 표준화(544a2977)
  // 이후 좌표는 baseScale(≈76~79)로 확대되는데 크기는 그대로 남아 2px — 주차면
  // (≈198px)의 1/100 이라 화면에서 보이지 않았다. 좌표와 같은 배율로 확대해
  // 표준화 이전의 주차면 대비 비율을 복원한다.
  if (
    layer.includes('cctv') || layer.includes('Cctv') ||
    layer.startsWith('p-emergency') || layer.startsWith('c-emergencybell')
  ) {
    size = { width: size.width * bounds.scale, height: size.height * bounds.scale }
  }
  const assetPath = getAssetPath(layer)

  // Check if we have a valid asset path (not the default one)
  const hasCustomAsset = assetPath !== '/assets/common.svg'

  if (hasCustomAsset) {
    // Use standardized ID
    return new shapes.standard.Image({
      id: elementId,
      position: { x: pos.x - size.width / 2, y: pos.y - size.height / 2 },
      size,
      attrs: {
        image: {
          xlinkHref: assetPath,
          opacity: 0.9
        },
        label: {
          text: points[0].text || '',
          fill: '#ffffff',
          fontSize: 10,
          refY: size.height + 5,
          refX: '50%',
          textAnchor: 'middle'
        }
      },
      data: {
        layer,
        entityHandle,
        originalId: entity.originalId || entityHandle,
        objectType: entity.objectType || layer,
        type: 'point',
        originalCoords: { x: centerX, y: centerY },
        text: points[0].text,
        properties: {
          name: entityHandle || ''
        }
      }
    })
  } else {
    // For point entities without specific assets, render as a circle
    // This ensures they're visible during layer mapping
    return new shapes.standard.Circle({
      id: elementId,
      position: { x: pos.x - size.width / 2, y: pos.y - size.height / 2 },
      size,
      attrs: {
        body: {
          fill: '#3b82f6',
          stroke: '#1e40af',
          strokeWidth: 2,
          opacity: 0.8
        },
        label: {
          text: points[0].text || '',
          fill: '#ffffff',
          fontSize: 10,
          refY: size.height + 5,
          refX: '50%',
          textAnchor: 'middle'
        }
      },
      data: {
        layer,
        entityHandle,
        originalId: entity.originalId || entityHandle,
        objectType: entity.objectType || layer,
        type: 'point',
        originalCoords: { x: centerX, y: centerY },
        text: points[0].text,
        properties: {
          name: entityHandle || ''
        }
      }
    })
  }
}


/**
 * Create line element (outlines, inner lines)
 */
function createLineElement(
  entity: GroupedEntity,
  bounds: { minX: number; minY: number; maxX: number; maxY: number; scale: number; flipX?: boolean }
): dia.Element | null {
  const { layer, points } = entity

  if (points.length < 2) return null

  // Transform coordinates
  const transformedCoords = points.map(p =>
    transformCoordinates(p.x, p.y, { minX: bounds.minX, minY: bounds.minY, maxX: bounds.maxX, maxY: bounds.maxY, scale: bounds.scale })
  )

  // Calculate bounding box
  const xs = transformedCoords.map(p => p.x)
  const ys = transformedCoords.map(p => p.y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)
  const maxX = Math.max(...xs)
  const maxY = Math.max(...ys)
  const width = maxX - minX
  const height = maxY - minY

  // Convert to relative coordinates
  const relativeCoords = transformedCoords.map(p => ({
    x: p.x - minX,
    y: p.y - minY
  }))

  // Create polyline using relative coordinates
  const pathData = relativeCoords
    .map((point, i) =>
      i === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`
    )
    .join(' ')

  // Create line element with explicit position and size
  // Use entityHandle as ID (e.g., "186F9") for consistency with export Name field
  return new shapes.standard.Path({
    id: entity.entityHandle,
    position: { x: minX, y: minY },
    size: { width: width || 1, height: height || 1 },
    attrs: {
      body: {
        d: pathData,
        fill: 'none',
        stroke: getLayerStrokeColor(layer),
        strokeWidth: entity.isClosed ? 2 : 1.5,
        opacity: 0.8
      }
    },
    data: {
      layer,
      entityHandle: entity.entityHandle,
      type: 'line',
      vertices: transformedCoords.map(p => [p.x, p.y] as [number, number]),
      // See createPolygonElement above — drag delta detection.
      originalPosition: { x: minX, y: minY },
      properties: {
        name: entity.entityHandle || ''
      }
    }
  })
}

/**
 * Create text/label element
 */
function createTextElement(
  entity: GroupedEntity,
  bounds: { minX: number; minY: number; maxX: number; maxY: number; scale: number; flipX?: boolean }
): dia.Element | null {
  const { layer, points } = entity

  if (points.length === 0 || !points[0].text) return null

  const point = points[0]
  const pos = transformCoordinates(point.x, point.y, { minX: bounds.minX, minY: bounds.minY, maxX: bounds.maxX, maxY: bounds.maxY, scale: bounds.scale })

  // Use entityHandle as ID (e.g., "186F9") for consistency with export Name field
  return new shapes.standard.Rectangle({
    id: point.entityHandle,
    position: { x: pos.x - 20, y: pos.y - 10 },
    size: { width: 40, height: 20 },
    attrs: {
      body: {
        fill: 'rgba(0, 0, 0, 0.6)',
        stroke: '#ffffff',
        strokeWidth: 1,
        rx: 3,
        ry: 3
      },
      label: {
        text: point.text,
        fill: '#ffffff',
        fontSize: 9,
        fontFamily: 'monospace',
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
        refX: '50%',
        refY: '50%'
      }
    },
    data: {
      layer,
      entityHandle: point.entityHandle,
      type: 'text',
      text: point.text,
      properties: {
        name: point.entityHandle || point.text || ''
      }
    }
  })
}

